<template>
    <v-dialog v-model="dialog" max-width="700px" persistent>
        <template v-slot:activator="{ on: dialogOn, attrs }">
            <template v-if="$scopedSlots.activator">
                <slot name="activator" :on="dialogOn" :attrs="attrs"></slot>
            </template>
            <v-btn v-else color="primary" v-bind="attrs" v-on="dialogOn" :disabled="!activeResourcePoolId">+ manager</v-btn>
        </template>

        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                Invite Resource Pool Managers
                <v-spacer></v-spacer>
                <v-btn @click="closeDialog" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <p>Invite managers to this resource pool.</p>
                <InviteEmailsInput
                    ref="inviteEmailsInput"
                    v-model="emailsInput"
                    :valid.sync="valid"
                    :disabled="inviteActionInProgress"
                    :showGenerateLinkOnlyToggle="true"
                    :generateInvitationLinksOnly.sync="generateInvitationLinksOnly"
                    :invitationErrorState="{
                        showDialog: showInvitationErrorsDialog,
                        errorType: validationErrorType,
                        invalidEmails: invalidEmailsList,
                        foundInvalidEmails,
                        backendError,
                        emailsValidationError
                    }"
                    @input="updateEmailString"
                    @proceedWithTheInvitation="inviteUsers($event.value)"
                    @validateEmailsAgain="validateEmailsAndInviteUsers($event.value)" />

                <v-alert v-if="error" color="error" class="mt-4" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Invitation Failure Error</span>
                        Failed to invite the following managers
                        <ul>
                            <li v-for="(email, index) in failedInvitations" :key="index">
                                {{ email }}
                            </li>
                        </ul>
                        <span v-if="invitationErrorContent">{{ invitationErrorContent }}</span>
                    </div>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <div>
                    <v-btn v-if="inviteSuccess" color="success" icon>
                        <v-icon>mdi-check</v-icon>
                    </v-btn>
                    <v-btn v-else :loading="inviteActionInProgress" @click="handleInvite" :disabled="!valid || inviteActionInProgress" color="primary" text>
                        Invite
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { inviteEmails } from '@/mixins/inviteEmails'
import InviteEmailsInput from '@/components/InviteEmailsInput.vue'

export default {
    name: 'ResourcePoolInviteDialog',
    mixins: [inviteEmails],
    components: { InviteEmailsInput },
    data() {
        return {
            dialog: false,
            inviteSuccess: false,
            inviteSuccessTimeoutId: null
        }
    },
    computed: {
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        activeResourcePoolId() {
            const routePoolId = Number(this.$route.params.rpid)
            if (Number.isFinite(routePoolId) && routePoolId > 0) {
                return routePoolId
            }

            const selectedPoolId = Number(this.selectedResourcePool)
            if (Number.isFinite(selectedPoolId) && selectedPoolId > 0) {
                return selectedPoolId
            }

            return Number(this.availableResourcePools[0]?.rpid || 0)
        },
        requestUrl() {
            if (!this.activeResourcePoolId) {
                return ''
            }

            return `/invitations/invite_resource_pool_manager/${this.activeResourcePoolId}`
        }
    },
    watch: {
        numInvitationsToSend(nextVal, prevVal) {
            if (prevVal > 0 && nextVal === 0 && !this.error) {
                this.inviteSuccess = true
                this.$emit('invited')
                if (this.inviteSuccessTimeoutId) {
                    clearTimeout(this.inviteSuccessTimeoutId)
                }
                this.inviteSuccessTimeoutId = setTimeout(() => {
                    this.closeDialog()
                }, 2000)
            }
        }
    },
    methods: {
        handleInvite() {
            this.inviteSuccess = false
            this.validateEmailsAndInviteUsers(true)
        },
        openDialog() {
            if (!this.activeResourcePoolId) {
                return
            }

            this.dialog = true
        },
        closeDialog() {
            if (this.inviteSuccessTimeoutId) {
                clearTimeout(this.inviteSuccessTimeoutId)
                this.inviteSuccessTimeoutId = null
            }
            this.dialog = false
            this.inviteSuccess = false
            this.resetInviteEmailsForm()
        }
    },
    beforeDestroy() {
        if (this.inviteSuccessTimeoutId) {
            clearTimeout(this.inviteSuccessTimeoutId)
        }
    }
}
</script>
